import React, { useState, useMemo } from 'react';
import {
  Table,
  Button,
  Space,
  Tag,
  Card,
  Row,
  Col,
  Statistic,
  Typography,
  Tooltip,
  Alert,
  message,
} from 'antd';
import {
  UserOutlined,
  SwapOutlined,
  FileSearchOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  PlusOutlined,
  EditOutlined,
} from '@ant-design/icons';
import { useCompany } from '../../../contexts/CompanyContext';
import { useSecureRequest } from '../../../hooks/useSecureRequest';
import {
  usePartnerBalances,
  useContacts,
  mutateContacts,
} from '../../../hooks/useAccounting';
import {
  PartnerBalanceSummary,
  TreasuryMovementType,
} from '../../../services/accountingService';
import { formatCurrency } from '../journal-entries/journalEntriesHelpers';
import PartnerLedgerDrawer from './PartnerLedgerDrawer';
import CreateTreasuryMovementModal from './CreateTreasuryMovementModal';
import { UnifiedContactModal } from '@ryo/ui';

const { Text } = Typography;

export const PartnerBalancesTab: React.FC = () => {
  const { company } = useCompany();
  const { securePost } = useSecureRequest();

  const { partnerBalances, isLoading, mutate } = usePartnerBalances({
    company_id: company.current?.id,
    company_uid: company.current?.uid || undefined,
  });

  const { contacts, mutate: mutateContactsHook } = useContacts({
    company_id: company.current?.id,
    company_uid: company.current?.uid || undefined,
  });

  // Modal & Drawer states
  const [movementModalOpen, setMovementModalOpen] = useState(false);
  const [movementType, setMovementType] = useState<TreasuryMovementType>('PARTNER_REIMBURSEMENT');
  const [selectedPartnerId, setSelectedPartnerId] = useState<number | null>(null);
  const [suggestedAmount, setSuggestedAmount] = useState<number | undefined>(undefined);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerContact, setDrawerContact] = useState<{ id: number; name: string } | null>(null);

  // Partner creation / edition modal state
  const [partnerModalOpen, setPartnerModalOpen] = useState(false);
  const [editingPartner, setEditingPartner] = useState<any>(null);

  const handleCreatePartner = () => {
    setEditingPartner(null);
    setPartnerModalOpen(true);
  };

  const handleEditPartner = async (partner: PartnerBalanceSummary) => {
    const found = contacts.find((c: any) => c.id === partner.contact_id);
    if (found) {
      setEditingPartner(found);
      setPartnerModalOpen(true);
      return;
    }
    try {
      const res = await securePost('/user/contact/get-contact-by-id', {
        company: company.current?.uid,
        id: partner.contact_id,
      });
      const contactData = res?.contact || res;
      const partnerName = partner.contact_name || (partner as any).name;
      const partnerDoc = partner.contact_document || (partner as any).document_number;
      setEditingPartner(contactData || { id: partner.contact_id, name: partnerName, document_number: partnerDoc });
    } catch {
      const partnerName = partner.contact_name || (partner as any).name;
      const partnerDoc = partner.contact_document || (partner as any).document_number;
      setEditingPartner({ id: partner.contact_id, name: partnerName, document_number: partnerDoc });
    }
    setPartnerModalOpen(true);
  };

  const handleSavePartnerContact = async (values: any) => {
    const isEdit = !!(values.uid || values.id);
    const dataRequest: any = {
      company: company.current?.uid,
      company_id: company.current?.id,
      module: 'ACCOUNTING',
      contact: {
        ...values,
        company_id: company.current?.id,
        roles: [{ role_type: 'PARTNER' }],
      },
    };

    let url = '/user/contact/create-contact';
    if (isEdit) {
      dataRequest.uidContact = values.uid;
      dataRequest.contact.id = values.id;
      url = '/user/contact/update-contact';
    } else {
      dataRequest.contact.roles = [{ role_type: 'PARTNER' }];
    }

    const res = await securePost(url, dataRequest);
    if (res?.status && res.status !== 200 && res.status !== 201) {
      throw new Error(res?.message || 'Error al guardar el socio');
    }
    message.success(isEdit ? 'Socio actualizado exitosamente' : 'Socio creado exitosamente con rol PARTNER');
    await mutateContactsHook();
    await mutateContacts(company.current?.id);
    await mutate();
    return res?.contact || res;
  };

  // Totals
  const totalPayable = useMemo(
    () => partnerBalances.reduce((s, p) => s + (Number(p.payable_to_partner) || 0), 0),
    [partnerBalances]
  );
  const totalReceivable = useMemo(
    () => partnerBalances.reduce((s, p) => s + (Number(p.receivable_from_partner) || 0), 0),
    [partnerBalances]
  );
  const totalNet = useMemo(
    () => totalPayable - totalReceivable,
    [totalPayable, totalReceivable]
  );

  const handleOpenReimbursement = (partner: PartnerBalanceSummary) => {
    setSelectedPartnerId(partner.contact_id);
    setMovementType('PARTNER_REIMBURSEMENT');
    setSuggestedAmount(partner.payable_to_partner > 0 ? partner.payable_to_partner : undefined);
    setMovementModalOpen(true);
  };

  const handleOpenAdvance = (partner: PartnerBalanceSummary) => {
    setSelectedPartnerId(partner.contact_id);
    setMovementType('PARTNER_ADVANCE');
    setSuggestedAmount(undefined);
    setMovementModalOpen(true);
  };

  const handleOpenNetting = (partner: PartnerBalanceSummary) => {
    setSelectedPartnerId(partner.contact_id);
    setMovementType('PARTNER_NETTING');
    const nettable = Math.min(partner.payable_to_partner, partner.receivable_from_partner);
    setSuggestedAmount(nettable > 0 ? nettable : undefined);
    setMovementModalOpen(true);
  };

  const handleOpenLedger = (partner: PartnerBalanceSummary) => {
    setDrawerContact({ id: partner.contact_id, name: partner.contact_name || (partner as any).name || 'Socio' });
    setDrawerOpen(true);
  };

  const columns = [
    {
      title: 'Socio / Accionista',
      dataIndex: 'contact_name',
      key: 'contact_name',
      render: (name: string, r: PartnerBalanceSummary) => {
        const displayName = name || (r as any).name || 'Socio';
        const displayDoc = r.contact_document || (r as any).document_number;
        return (
          <Space>
            <UserOutlined style={{ color: '#13c2c2', fontSize: 16 }} />
            <div>
              <Text strong>{displayName}</Text>
              {displayDoc && (
                <div>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    CC / NIT: {displayDoc}
                  </Text>
                </div>
              )}
            </div>
          </Space>
        );
      },
    },
    {
      title: 'Deuda a Favor del Socio (Pasivo 2355)',
      dataIndex: 'payable_to_partner',
      key: 'payable_to_partner',
      align: 'right' as const,
      render: (val: number) => {
        const hasBalance = (val || 0) > 0;
        return (
          <div>
            <Text strong style={{ color: hasBalance ? '#52c41a' : '#bfbfbf', fontSize: 14 }}>
              {formatCurrency(val || 0)}
            </Text>
            {hasBalance && (
              <div>
                <Tag color="success" style={{ fontSize: 10, lineHeight: '16px' }}>
                  Empresa debe al socio
                </Tag>
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Deuda del Socio con la Empresa (Activo 1325)',
      dataIndex: 'receivable_from_partner',
      key: 'receivable_from_partner',
      align: 'right' as const,
      render: (val: number) => {
        const hasBalance = (val || 0) > 0;
        return (
          <div>
            <Text strong style={{ color: hasBalance ? '#fa8c16' : '#bfbfbf', fontSize: 14 }}>
              {formatCurrency(val || 0)}
            </Text>
            {hasBalance && (
              <div>
                <Tag color="warning" style={{ fontSize: 10, lineHeight: '16px' }}>
                  Socio debe a la empresa
                </Tag>
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Posición Neta',
      dataIndex: 'net_balance',
      key: 'net_balance',
      align: 'right' as const,
      render: (val: number) => {
        const net = Number(val) || 0;
        const color = net > 0 ? '#52c41a' : net < 0 ? '#f5222d' : '#8c8c8c';
        return (
          <div>
            <Text strong style={{ color, fontSize: 14 }}>
              {formatCurrency(Math.abs(net))}
            </Text>
            <div>
              <Text type="secondary" style={{ fontSize: 11 }}>
                {net > 0 ? 'A favor del socio' : net < 0 ? 'A favor de la empresa' : 'Saldada'}
              </Text>
            </div>
          </div>
        );
      },
    },
    {
      title: 'Acciones de Flujo y Extracto',
      key: 'actions',
      align: 'right' as const,
      render: (_: any, r: PartnerBalanceSummary) => {
        const canReimburse = r.payable_to_partner > 0;
        const canNet = r.payable_to_partner > 0 && r.receivable_from_partner > 0;

        return (
          <Space size={6} wrap>
            <Tooltip title="Pagar deuda acumulada al socio desde bancos o caja de la empresa">
              <Button
                type="primary"
                size="small"
                disabled={!canReimburse}
                style={{ backgroundColor: canReimburse ? '#52c41a' : undefined, borderColor: canReimburse ? '#52c41a' : undefined }}
                onClick={() => handleOpenReimbursement(r)}
              >
                Reembolsar
              </Button>
            </Tooltip>

            <Tooltip title="Entregar anticipo de utilidades o pagar gasto personal del socio">
              <Button size="small" onClick={() => handleOpenAdvance(r)}>
                Anticipo
              </Button>
            </Tooltip>

            {canNet && (
              <Tooltip title="Cruzar directamente la deuda 2355 contra el anticipo 1325 sin mover bancos">
                <Button
                  size="small"
                  icon={<SwapOutlined />}
                  style={{ color: '#eb2f96', borderColor: '#eb2f96' }}
                  onClick={() => handleOpenNetting(r)}
                >
                  Cruce
                </Button>
              </Tooltip>
            )}

            <Button
              size="small"
              icon={<FileSearchOutlined />}
              onClick={() => handleOpenLedger(r)}
            >
              Extracto
            </Button>

            <Tooltip title="Ver y editar datos maestros del socio (Cédula, nombre, teléfono)">
              <Button
                size="small"
                icon={<EditOutlined />}
                onClick={() => handleEditPartner(r)}
              >
                Editar
              </Button>
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  return (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        <Col xs={24} sm={8}>
          <Card size="small" style={{ background: '#f6ffed' }}>
            <Statistic
              title="Total Deuda con Socios (Pasivo PUC 2355)"
              value={totalPayable}
              formatter={(val) => formatCurrency(Number(val))}
              valueStyle={{ color: '#52c41a', fontWeight: 600 }}
              prefix={<ArrowUpOutlined />}
            />
            <Text type="secondary" style={{ fontSize: 11 }}>
              Compras y gastos pagados por socios pendientes de reembolso
            </Text>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small" style={{ background: '#fff7e6' }}>
            <Statistic
              title="Total Cuentas por Cobrar a Socios (Activo PUC 1325)"
              value={totalReceivable}
              formatter={(val) => formatCurrency(Number(val))}
              valueStyle={{ color: '#fa8c16', fontWeight: 600 }}
              prefix={<ArrowDownOutlined />}
            />
            <Text type="secondary" style={{ fontSize: 11 }}>
              Anticipos de utilidades o gastos personales pagados por la empresa
            </Text>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card
            size="small"
            variant="outlined"
            style={{
              background: totalNet >= 0 ? '#f6ffed' : '#fff1f0',
              borderColor: totalNet >= 0 ? '#b7eb8f' : '#ffa39e',
            }}
          >
            <Statistic
              title="Posición Neta Global de Socios"
              value={totalNet}
              formatter={(val) => formatCurrency(Number(val))}
              valueStyle={{ color: totalNet >= 0 ? '#52c41a' : '#f5222d', fontWeight: 700 }}
            />
            <Text type="secondary" style={{ fontSize: 11 }}>
              {totalNet >= 0
                ? 'La empresa tiene deuda neta a favor de los socios'
                : 'Los socios tienen deuda neta a favor de la empresa'}
            </Text>
          </Card>
        </Col>
      </Row>

      <Alert
        message="Estado de las Cuentas Corrientes de Socios"
        description="Cuentas relacionadas a socios por pagos, anticipos, retiros de utilidades y gastos personales."
        type="info"
        showIcon
        style={{ marginBottom: 16, fontSize: 12 }}
      />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <Text strong style={{ fontSize: 16 }}>
            Listado de Socios y Cuentas Corrientes
          </Text>
          <div style={{ fontSize: 12, color: '#8c8c8c' }}>
            Socios con movimientos en las cuentas contables 1325 (Por cobrar) y 2355 (Por pagar).
          </div>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleCreatePartner}
        >
          Nuevo Socio
        </Button>
      </div>

      <Table
        rowKey="contact_id"
        columns={columns}
        dataSource={partnerBalances}
        loading={isLoading}
        pagination={{ pageSize: 10 }}
        size="middle"
        locale={{
          emptyText:
            'No hay saldos registrados con socios. Cuando se registre una compra pagada por un socio o un anticipo, aparecerán aquí.',
        }}
      />

      {/* Modal Operación de Tesorería */}
      <CreateTreasuryMovementModal
        open={movementModalOpen}
        onClose={() => setMovementModalOpen(false)}
        onSuccess={() => {
          void mutate();
        }}
        defaultMovementType={movementType}
        defaultContactId={selectedPartnerId}
        suggestedAmount={suggestedAmount}
      />

      {/* Drawer Extracto Auxiliar */}
      {drawerContact && (
        <PartnerLedgerDrawer
          open={drawerOpen}
          onClose={() => {
            setDrawerOpen(false);
            setDrawerContact(null);
          }}
          contactId={drawerContact.id}
          contactName={drawerContact.name}
        />
      )}

      {/* Modal Crear / Editar Socio */}
      <UnifiedContactModal
        visible={partnerModalOpen}
        onCancel={() => {
          setPartnerModalOpen(false);
          setEditingPartner(null);
        }}
        onSuccess={() => {
          setPartnerModalOpen(false);
          setEditingPartner(null);
        }}
        initialValues={editingPartner || undefined}
        onSave={handleSavePartnerContact}
        role="PARTNER"
        validationMode="strict"
      />
    </div>
  );
};

export default PartnerBalancesTab;
